import { Toaster } from "@/components/ui/toaster";
import { StyledEngineProvider } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import StylesProvider from "@mui/styles/StylesProvider";
import DocumentViewer from "components/Feature/ContentSection/LegalDocuments/DocumentViewer";
import Settings from "components/Feature/ContentSection/Settings";
import AboutPage from "components/HomePage/About";
import Contact from "components/HomePage/Contact";
import EmailVerification from "components/HomePage/EmailVerification";
import Features from "components/HomePage/Features";
import Pricing from "components/HomePage/Pricing";
import InviteActivation from "components/InviteActivation";
import React from "react";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { CustomProvider } from "rsuite";
import frFR from "rsuite/locales/fr_FR";
import "../../config/Language/i18n.ts";
import { CustomTheme } from "../../styles/Theme";
import { Feature } from "../Feature";
import Certifications from "../Feature/ContentSection/Certifications";
import CertificationDetails from "../Feature/ContentSection/Certifications/CertificationDetails";
import RespondToCertification from "../Feature/ContentSection/Certifications/RespondToCertification";
import ChatPage from "../Feature/ContentSection/ChatPage";
import ExpertManagement from "../Feature/ContentSection/Admin/Experts";
import LegalDocuments from "../Feature/ContentSection/LegalDocuments";
import EditExpertPage from "../Feature/ContentSection/Profile/EditExpertPage";
import ProfilePage from "../Feature/ContentSection/Profile/ProfilePage";
import HomePage from "../HomePage";
import { AuthProvider } from "../Reusable/AuthProvider";
import { ProtectedRoute } from "../Reusable/ProtectedRoute";
import ScrollToTop from "../Reusable/ScrollToTop";
import { SettingsProvider } from "../Reusable/SettingsProvider";
import { ThemeProvider as CustomThemeProvider } from "../Reusable/ThemeProvider";
import SignIn from "../SignIn";
import SignUp from "../SignUp";
import ForgotPasswordForm from "components/SignIn/ForgotPasswordForm";

export interface AppProps {}

const App: React.FC<AppProps> = () => {
  return (
    <StyledEngineProvider injectFirst>
      <StylesProvider>
        <ThemeProvider theme={CustomTheme}>
          <CustomProvider locale={frFR}>
            <Router>
              <ScrollToTop />
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/features" element={<Features />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/signin" element={<SignIn />} />
                <Route path="/forgot-password" element={<ForgotPasswordForm />} />
                <Route
                  path="/email-verification/:uuid"
                  element={<EmailVerification />}
                />
                <Route path="/activate/:uuid" element={<InviteActivation />} />
                <Route
                  path="/feature"
                  element={
                    <AuthProvider>
                      <SettingsProvider>
                        <CustomThemeProvider>
                          <Feature />
                        </CustomThemeProvider>
                      </SettingsProvider>
                    </AuthProvider>
                  }
                >
                  <Route
                    path="/feature/chat/:conversationId?"
                    element={
                      <ProtectedRoute requiredRoles={["USER"]}>
                        <ChatPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/feature/legalDocuments"
                    element={<LegalDocuments />}
                  />
                  <Route
                    path="/feature/legalDocuments/pdf-viewer/:docId"
                    element={<DocumentViewer />}
                  />
                  <Route
                    path="/feature/certifications"
                    element={
                      <ProtectedRoute requiredRoles={["USER", "EXPERT"]}>
                        <Certifications />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/feature/certifications/respond/:certificationId"
                    element={
                      <ProtectedRoute requiredRoles={["EXPERT"]}>
                        <RespondToCertification />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/feature/certifications/details/:certificationId"
                    element={
                      <ProtectedRoute requiredRoles={["USER", "EXPERT"]}>
                        <CertificationDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/feature/certifications/details/:certificationId"
                    element={
                      <ProtectedRoute requiredRoles={["USER", "EXPERT"]}>
                        <CertificationDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/feature/profile/:userId"
                    element={<ProfilePage />}
                  />
                  <Route
                    path="/feature/profile/edit"
                    element={<EditExpertPage />}
                  />
                  <Route path="/feature/settings" element={<Settings />} />
                  <Route
                    path="/feature/admin/experts"
                    element={
                      <ProtectedRoute requiredRoles={["ADMIN"]}>
                        <ExpertManagement />
                      </ProtectedRoute>
                    }
                  />
                </Route>
              </Routes>
              <Toaster />
            </Router>
          </CustomProvider>
        </ThemeProvider>
      </StylesProvider>
    </StyledEngineProvider>
  );
};

export default App;
